import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { IoChevronDown, IoMenu } from 'react-icons/io5'
import { RiMoonClearLine, RiSparkling2Line } from 'react-icons/ri'

import { useAuth } from '@/core/hooks/use-auth'
import { signOut } from '@/core/utils/auth-utils'
import { useOutsideClick } from '@/core/hooks/use-outside-click'
import { useTheme } from '@/core/hooks/use-theme'

type THeaderProps = {
  setSidebarOpen: (sidebarOpen: boolean) => void
}

// Motion'suz port (dashboard `motion/react` yerine Tailwind transition). Tema
// geçişinin asıl animasyonu zaten store'daki native View Transitions API ile yapılır.
const Header = ({ setSidebarOpen }: THeaderProps) => {
  const navigate = useNavigate()
  const [profileOpen, setProfileOpen] = useState<boolean>(false)
  const ref = useOutsideClick(() => setProfileOpen(false))

  const { user } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const isCanli = theme === 'canli'

  // Çıkış: backend refresh cookie'sini sildirir + client access token'ı temizler, sonra yönlendirir.
  const handleSignOut = async () => {
    setProfileOpen(false)
    await signOut()
    await navigate({ to: '/' })
  }

  const headerClass = isCanli
    ? 'fixed top-0 right-0 left-0 z-1 flex h-16 items-center justify-between border-b border-[var(--app-border)] bg-[var(--app-surface)]/80 px-4 backdrop-blur-md lg:px-6'
    : 'fixed top-0 right-0 left-0 z-1 flex h-16 items-center justify-between bg-white px-4 shadow-lg lg:px-6'

  const menuBtnClass = isCanli
    ? 'rounded-md p-1 text-[var(--app-text)] hover:bg-[var(--app-surface-2)] lg:hidden'
    : 'rounded-md p-1 hover:bg-gray-100 lg:hidden'

  const nameClass = isCanli
    ? 'ml-2 text-sm font-medium capitalize text-[var(--app-text)]'
    : 'ml-2 text-sm font-medium text-gray-700 capitalize'

  const themeBtnClass = isCanli
    ? 'relative inline-flex h-9 cursor-pointer items-center gap-2 overflow-hidden rounded-full border border-[var(--app-border)] bg-gradient-to-r from-[#7c5cff] via-[#22d3ee] to-[#ec4899] px-3 text-xs font-semibold text-white shadow-[0_8px_24px_-8px_rgba(124,92,255,0.7)] transition-transform duration-200 hover:scale-105 active:scale-90'
    : 'relative inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 transition-transform duration-200 hover:scale-105 hover:bg-gray-50 active:scale-90'

  const dropdownClass = isCanli
    ? 'absolute right-0 mt-2 w-48 origin-top-right rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)] py-1 shadow-2xl transition focus:outline-none'
    : 'absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg transition focus:outline-none'

  const signOutBtnClass = isCanli
    ? 'block w-full cursor-pointer px-4 py-2 text-left text-sm text-[var(--app-text)] hover:bg-[var(--app-surface-2)]'
    : 'block w-full cursor-pointer px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100'

  return (
    <header className={headerClass}>
      <div className="flex items-center">
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          data-testid="open-sidebar"
          aria-label="Menüyü aç"
          className={menuBtnClass}
        >
          <IoMenu className="h-6 w-6" />
        </button>
      </div>
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={(event) =>
            toggleTheme({ clientX: event.clientX, clientY: event.clientY })
          }
          data-testid="theme-toggle"
          aria-label="Tema değiştir"
          className={themeBtnClass}
        >
          <span className="inline-flex items-center gap-2">
            {isCanli ? (
              <>
                <RiSparkling2Line className="h-4 w-4" />
                Canlı
              </>
            ) : (
              <>
                <RiMoonClearLine className="h-4 w-4" />
                Klasik
              </>
            )}
          </span>
        </button>
        <div ref={ref} className="flex cursor-pointer items-center">
          <div className="relative">
            <button
              type="button"
              data-testid="profile-button"
              className="flex cursor-pointer items-center rounded-full text-sm outline-none"
              onClick={() => setProfileOpen(!profileOpen)}
            >
              <span data-testid="profile-name" className={nameClass}>
                {user?.name}
              </span>
              <IoChevronDown
                className={isCanli ? 'ml-1 text-[var(--app-text-dim)]' : 'ml-1'}
              />
            </button>
            {profileOpen && (
              <div data-testid="profile-dropdown" className={dropdownClass}>
                <button
                  type="button"
                  data-testid="signout-button"
                  onClick={handleSignOut}
                  className={signOutBtnClass}
                >
                  Çıkış Yap
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
